import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Box, FormControl, IconButton, Stack, Typography } from "@mui/material";
import { validateStringField } from "../../../utils/Validation";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

export interface MaintenanceTemplateFormData {
  name: string;
  defaultMessage: string;
  localizedMessages: {
    [locale: string]: string;
  };
}

export interface MaintenanceTemplateFormProps {
  visible: boolean;
  mode: "create" | "edit";
  initialData?: MaintenanceTemplateFormData;
  onAccept: (data: MaintenanceTemplateFormData) => Promise<void>;
  onDismiss: () => void;
}

interface LocalizedMessageEntry {
  locale: string;
  message: string;
}

const MaintenanceTemplateForm = ({ visible, mode, initialData, onAccept, onDismiss }: MaintenanceTemplateFormProps) => {
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [defaultMessageError, setDefaultMessageError] = useState<string | undefined>(undefined);
  const [localizedMessages, setLocalizedMessages] = useState<LocalizedMessageEntry[]>([]);

  const refName = useRef<TextFieldProps>(null);
  const refDefaultMessage = useRef<TextFieldProps>(null);

  useEffect(() => {
    if (visible && initialData) {
      // Convert localizedMessages object to array
      const entries = Object.entries(initialData.localizedMessages || {}).map(([locale, message]) => ({
        locale,
        message,
      }));
      setLocalizedMessages(entries);
    } else if (!visible) {
      // Reset when dialog closes
      setLocalizedMessages([]);
      setNameError(undefined);
      setDefaultMessageError(undefined);
    }
  }, [visible, initialData]);

  const dialogCloseHandler = (event: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const okButtonClickHandler = async () => {
    if (!refName.current || !refDefaultMessage.current) {
      return;
    }

    const name = validateStringField(refName.current.value as string, setNameError, 1, 100);
    const defaultMessage = validateStringField(refDefaultMessage.current.value as string, setDefaultMessageError, 1, 500);

    if (!name || !defaultMessage) {
      return;
    }

    // Convert array back to object
    const localizedMessagesObj: { [locale: string]: string } = {};
    localizedMessages.forEach((entry) => {
      if (entry.locale && entry.message) {
        localizedMessagesObj[entry.locale] = entry.message;
      }
    });

    const data: MaintenanceTemplateFormData = {
      name,
      defaultMessage,
      localizedMessages: localizedMessagesObj,
    };

    setLoading(true);
    try {
      await onAccept(data);
    } finally {
      setLoading(false);
    }
  };

  const addLocalizedMessage = () => {
    setLocalizedMessages([...localizedMessages, { locale: "", message: "" }]);
  };

  const removeLocalizedMessage = (index: number) => {
    const newMessages = [...localizedMessages];
    newMessages.splice(index, 1);
    setLocalizedMessages(newMessages);
  };

  const updateLocalizedMessage = (index: number, field: "locale" | "message", value: string) => {
    const newMessages = [...localizedMessages];
    newMessages[index][field] = value;
    setLocalizedMessages(newMessages);
  };

  return (
    <Dialog open={visible} onClose={dialogCloseHandler} maxWidth="md" fullWidth>
      <DialogTitle>{mode === "create" ? "Create Maintenance Template" : "Edit Maintenance Template"}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {mode === "create"
            ? "Fill in the details to create a new maintenance template."
            : "Update the maintenance template details."}
        </DialogContentText>
        <FormControl sx={{ m: 1, minWidth: 540 }} fullWidth>
          <TextField
            margin="dense"
            fullWidth
            variant="standard"
            inputRef={refName}
            id="name"
            label="Template Name"
            type="text"
            defaultValue={initialData?.name || ""}
            disabled={mode === "edit"}
            error={nameError !== undefined}
            helperText={nameError || (mode === "edit" ? "Template name cannot be changed" : "")}
          />
        </FormControl>
        <FormControl sx={{ m: 1, minWidth: 540 }} fullWidth>
          <TextField
            margin="dense"
            fullWidth
            variant="standard"
            inputRef={refDefaultMessage}
            id="defaultMessage"
            label="Default Message"
            type="text"
            multiline
            rows={3}
            defaultValue={initialData?.defaultMessage || ""}
            error={defaultMessageError !== undefined}
            helperText={defaultMessageError}
          />
        </FormControl>

        <Box sx={{ m: 1, mt: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Localized Messages</Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addLocalizedMessage}>
              Add Localized Message
            </Button>
          </Stack>

          {localizedMessages.map((entry, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                label="Locale (e.g., en-US)"
                variant="outlined"
                size="small"
                value={entry.locale}
                onChange={(e) => updateLocalizedMessage(index, "locale", e.target.value)}
                sx={{ width: "200px" }}
              />
              <TextField
                label="Message"
                variant="outlined"
                size="small"
                multiline
                rows={2}
                value={entry.message}
                onChange={(e) => updateLocalizedMessage(index, "message", e.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <IconButton color="error" onClick={() => removeLocalizedMessage(index)}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}

          {localizedMessages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No localized messages added. Click &quot;Add Localized Message&quot; to add one.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="error" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={okButtonClickHandler} disabled={loading}>
          {mode === "create" ? "Create" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceTemplateForm;
